import { useCallback, useMemo, useState, useEffect, useRef } from 'react'
import { Skeleton, Autocomplete, Grid, TextField, Typography } from '@mui/material'
import { getRequestConfig } from '../auth/auth'
import { ApiGateway } from '../config/config'
import { ROLES } from '../auth/role'
import axios from 'axios'

const DEFAULT_MANAGER_ROLES = [ROLES.QSE_MGR]

export default function QSEAutoComplete(props = { initializeDefaultUser: false, defaultUserId: undefined, onChange: undefined, label: undefined, placeholder: undefined, roles: [] | undefined }) {
  const [userList, setUserList] = useState([])
  const [userAcList, setUserAcList] = useState([])
  const [userId, setUserId] = useState('')
  const [loading, setLoading] = useState(false)
  const initializedDefaultUserIdRef = useRef(undefined)
  // const loading = false
  const { onChange: onChangeFromProps } = props
  const roleIdsSignature = useMemo(() => {
    const configuredRoles = (Array.isArray(props.roles) && props.roles.length > 0) ? props.roles : DEFAULT_MANAGER_ROLES
    const uniqueRoleIds = [...new Set(configuredRoles.map((role) => role?.id).filter(Boolean))]

    if (!uniqueRoleIds.includes(ROLES.MFI_ADMIN.id)) {
      uniqueRoleIds.push(ROLES.MFI_ADMIN.id)
    }

    return uniqueRoleIds.sort().join('|')
  }, [props.roles])

  const roleIdsToLoad = useMemo(() => roleIdsSignature ? roleIdsSignature.split('|') : [], [roleIdsSignature])

  const loadUsers = useCallback(async () => {
    setLoading(true)
    const requestConfig = await getRequestConfig()
    const basePath = `${ApiGateway.user}/search`

    try {
      const workers = roleIdsToLoad.map((roleId) => axios.post(basePath, { roleId }, requestConfig))
      const responses = await Promise.all(workers)
      const qseData = responses.flatMap((resp) => resp.data)
      
      setUserList(qseData)
      setUserAcList(qseData.filter(user => user.isActive !== false ))
    } catch (error) {
      setUserList([])
      setUserAcList([])
    }

    setLoading(false)
  }, [roleIdsSignature])

  useEffect(() => {
    loadUsers()
  }, [loadUsers])

  useEffect(() => {
    if(userList?.length) {
      if(props.defaultUserId) {
        const defaultUserId = `${props.defaultUserId}`
        if (initializedDefaultUserIdRef.current === defaultUserId) {
          return
        }

        const currentUser = userList.find(assessor => assessor.koId === props.defaultUserId)
        if(currentUser) {
          setUserId(currentUser.koId)
          initializedDefaultUserIdRef.current = defaultUserId
        }

        if(onChangeFromProps && props.initializeDefaultUser) {
          onChangeFromProps(props.defaultUserId)
        }
      } 
    }
  }, [userList, props.defaultUserId, onChangeFromProps, props.initializeDefaultUser])

  const user = useMemo(() =>
    userList.find(u => `${u.koId}` === `${userId}`) || null,
    [userList, userId]
  )

  return loading ? <Skeleton sx={{height: '37.8px'}}/> : <Autocomplete
    id="user"
    getOptionLabel={(option) => `${option?.firstName} ${option?.lastName} (${option?.koId})` || {}}
    filterOptions={(x) => x}
    options={userAcList}
    autoComplete
    size='small'
    value={user}
    noOptionsText="No results"
    onChange={(event, newValue) => {
      setUserId(newValue?.koId)
      onChangeFromProps?.(newValue?.koId)
    }}
    onInputChange={(_event, newInputValue, reason) => {
      const activeUsers = userList.filter(user => user.isActive !== false)

      // Keep the full list when autocomplete updates input due to selection/reset events.
      if (reason !== 'input' || !newInputValue) {
        setUserAcList(activeUsers)
        return
      }

      const lowerInput = (newInputValue || '').toLowerCase()
      const newUserList = activeUsers.filter(user => {
        const firstName = (user.firstName || '').toLowerCase()
        const lastName = (user.lastName || '').toLowerCase()
        const koId = (user.koId || '').toLowerCase()
        return firstName.includes(lowerInput) || lastName.includes(lowerInput) || koId.includes(lowerInput)
      })

      setUserAcList(newUserList)
    }}
    renderInput={(params) => (
      <TextField {...params} value={params.value || ''} label={props.label === false ? undefined : (props.label || 'QSE Manager')} fullWidth placeholder={props.placeholder === false ? undefined : (props.placeholder || 'QSE Manager')} />
    )}
    renderOption={(props, option) => {
      return (
        <li {...props} key={props.key}>
          <Grid container alignItems="center">
            <Grid item sx={{ width: 'calc(100% - 44px)', wordWrap: 'break-word' }}>
              <Typography variant="body2" color="text.secondary">
                {option?.firstName} {option?.lastName} ({option?.koId})
              </Typography>
            </Grid>
          </Grid>
        </li>
      );
    }}
  />
}